import React, { FC, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';

import { Box } from '@mui/material';

import Router from './router';
import AuthProvider from './store';
import { loadCSS, loadCSSAsync } from '@shared/utils/load-css';
import { ApiLogger } from '@shared/components/ApiLogger';

import './App.scss';

// react-toastify CSS загружается динамически, чтобы не блокировать рендеринг
// В production плагин deferNonCriticalCSS обработает это автоматически
// В development загружаем асинхронно через утилиту

export const App: FC = () => {
  // Отложенная загрузка CSS для react-toastify
  // Загружаем только когда компонент монтируется, чтобы не блокировать первый рендер
  // Плагин deferNonCriticalCSS удаляет CSS из HTML, поэтому загружаем динамически
  useEffect(() => {
    // Проверяем, не загружен ли уже CSS
    const existingLink = document.querySelector('link[href*="ReactToastify"]');
    if (existingLink) {
      return; // Уже загружен
    }
    
    if (import.meta.env.PROD) {
      // В production ищем CSS файл в собранных ассетах
      // Vite генерирует файлы с хешем, например: react-toastify-93d8b085.css
      // Пробуем найти CSS файл через поиск в HTML или используем динамический импорт
      
      // Сначала пробуем найти CSS файл в уже загруженных ссылках
      const findCSSInDocument = () => {
        const links = document.querySelectorAll('link[rel="stylesheet"]');
        for (const link of links) {
          const href = link.getAttribute('href') || '';
          if (href.includes('react-toastify') || href.includes('ReactToastify')) {
            return href;
          }
        }
        return null;
      };
      
      const existingCSS = findCSSInDocument();
      if (existingCSS) {
        // CSS уже есть в документе
        return;
      }
      
      // Пробуем найти CSS файл через поиск в HTML
      // Если не найдем, просто пропускаем - toast будет работать без стилей
      fetch(window.location.href)
        .then(res => res.text())
        .then(html => {
          // Ищем все CSS файлы с react-toastify в имени
          const cssRegex = /<link[^>]*href="([^"]*react-toastify[^"]*\.css[^"]*)"[^>]*>/gi;
          const matches = [...html.matchAll(cssRegex)];
          
          if (matches.length > 0) {
            const cssUrl = matches[0][1];
            // Если путь относительный, делаем его абсолютным
            const fullUrl = cssUrl.startsWith('http') 
              ? cssUrl 
              : new URL(cssUrl, window.location.origin).href;
            loadCSS(fullUrl, 'react-toastify-css').catch(() => {
              console.warn('⚠️ [App] Не удалось загрузить react-toastify CSS из HTML');
            });
          } else {
            console.warn('⚠️ [App] react-toastify CSS не найден в HTML. Toast может работать без стилей.');
          }
        })
        .catch(() => {
          console.warn('⚠️ [App] Не удалось получить HTML для поиска CSS');
        });
    } else {
      // В development загружаем напрямую из node_modules асинхронно
      loadCSSAsync('/node_modules/react-toastify/dist/ReactToastify.css', 'react-toastify-css');
    }
  }, []);

  return (
    <Box
      style={{
        display: 'flex',
        minHeight: 'var(--app-height)',
        width: '100%',
        flexDirection: 'column',
        backgroundColor: 'white',
      }}
    >
      <ToastContainer />
      <AuthProvider>
        <Router />
      </AuthProvider>
      <ApiLogger />
    </Box>
  );
};
